import React, { useCallback, useEffect, useState } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getClientTransport } from '../handlers/ClientTransportHandler';
import { isSessionDead } from '../lib/sessionDead';
import { signOut } from './AuthView';
import {
  clearBatonBannerDismiss,
  dismissBatonBanner,
  isBatonBannerDismissed,
} from '../lib/batonConnectionBannerState';

/**
 * Non-blocking reconnect indicator when BATON WebSocket is down.
 * Sits below the sticky app header; dismissible; never intercepts header/menu clicks.
 * Auth failures use ServiceSessionDeadBanner instead (reconnect halted via markSessionDead).
 */
export default function BatonConnectionBanner() {
  const connectionState = useMaestroStore((s) => s.system.connectionState);
  const [retryBusy, setRetryBusy] = useState(false);
  const [signOutBusy, setSignOutBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => isBatonBannerDismissed());

  const outageActive = connectionState === 'reconnecting' || connectionState === 'error';
  const showBanner = !isSessionDead() && outageActive && !dismissed;

  useEffect(() => {
    if (connectionState === 'connected') {
      clearBatonBannerDismiss();
      setDismissed(false);
    }
  }, [connectionState]);

  useEffect(() => {
    if (outageActive && isBatonBannerDismissed()) {
      setDismissed(true);
    }
  }, [outageActive]);

  const onDismiss = useCallback(() => {
    dismissBatonBanner();
    setDismissed(true);
  }, []);

  const onRetry = useCallback(async () => {
    const transport = getClientTransport();
    if (!transport.canManualReconnect()) return;
    setRetryBusy(true);
    try {
      await transport.resumeReconnectAfterNetworkRecovery('manual');
    } finally {
      setRetryBusy(false);
    }
  }, []);

  const onSignOut = useCallback(async () => {
    setSignOutBusy(true);
    try {
      await signOut();
    } finally {
      setSignOutBusy(false);
    }
  }, []);

  if (!showBanner) {
    return null;
  }

  const isExhausted = connectionState === 'error';
  const label = isExhausted
    ? 'Connection lost'
    : 'Reconnecting…';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 45,
        pointerEvents: 'none',
      }}
    >
      <div
        role="status"
        aria-live="polite"
        data-testid="baton-connection-banner"
        style={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          top: 76,
          zIndex: 45,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          flexWrap: 'wrap',
          maxWidth: 'min(720px, calc(100vw - 32px))',
          padding: '10px 14px',
          borderRadius: 12,
          background: 'rgba(20, 20, 24, 0.94)',
          color: '#f5f5f5',
          fontSize: 13,
          fontWeight: 500,
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        }}
      >
        {!isExhausted && (
          <span
            aria-hidden
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.35)',
              borderTopColor: '#fff',
              animation: 'maestro-baton-spin 0.8s linear infinite',
              flexShrink: 0,
            }}
          />
        )}
        <span style={{ textAlign: 'center' }}>{label}</span>
        <button
          type="button"
          onClick={onRetry}
          disabled={retryBusy || signOutBusy}
          style={{
            padding: '4px 12px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.35)',
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: retryBusy ? 'wait' : 'pointer',
          }}
        >
          {retryBusy ? 'Retrying…' : 'Retry'}
        </button>
        {isExhausted && (
          <button
            type="button"
            onClick={onSignOut}
            disabled={retryBusy || signOutBusy}
            style={{
              padding: '4px 12px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 13,
              fontWeight: 500,
              cursor: signOutBusy ? 'wait' : 'pointer',
            }}
          >
            {signOutBusy ? 'Signing out…' : 'Sign out'}
          </button>
        )}
        <button
          type="button"
          aria-label="Dismiss connection notice"
          data-testid="baton-connection-banner-dismiss"
          onClick={onDismiss}
          disabled={retryBusy || signOutBusy}
          style={{
            padding: '4px 8px',
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: 'rgba(255,255,255,0.65)',
            fontSize: 18,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        <style>{`
          @keyframes maestro-baton-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
